import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import HardLink from "@/components/HardLink";

const CartDrawer = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, totalPrice, totalItems } = useCart();

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-lg bg-card border-border flex flex-col">
        <SheetHeader className="border-b border-border pb-4">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <ShoppingBag className="w-5 h-5 text-primary" />
            Winkelwagen
            <span className="text-sm text-muted-foreground">({totalItems} items)</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
              <ShoppingBag className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Je winkelwagen is leeg</h3>
            <p className="text-muted-foreground mb-6">Voeg producten toe om te beginnen met winkelen</p>
            <Button variant="glow" onClick={() => setIsCartOpen(false)} asChild>
              <HardLink to="/shop">
                Bekijk producten
                <ArrowRight className="w-4 h-4 ml-2" />
              </HardLink>
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.map((item) => (
                <div key={item.product.id} className="flex gap-4 p-4 bg-secondary/50 rounded-lg">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground truncate">{item.product.name}</h4>
                    <p className="text-primary font-bold">€{item.product.price.toFixed(2)}</p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center hover:bg-primary/20 transition-colors"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center hover:bg-primary/20 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-border pt-4 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotaal</span>
                  <span className="text-foreground">€{totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Verzending</span>
                  <span className="text-foreground">{totalPrice >= 50 ? "Gratis" : "€4.95"}</span>
                </div>
                <div className="flex items-center justify-between font-bold text-lg pt-2 border-t border-border">
                  <span className="text-foreground">Totaal</span>
                  <span className="text-primary">
                    €{(totalPrice + (totalPrice >= 50 ? 0 : 4.95)).toFixed(2)}
                  </span>
                </div>
              </div>
              
              {totalPrice < 50 && (
                <p className="text-xs text-center text-muted-foreground">
                  Nog €{(50 - totalPrice).toFixed(2)} voor gratis verzending!
                </p>
              )}

              <Button variant="glow" size="lg" className="w-full" asChild>
                <HardLink to="/checkout" onClick={() => setIsCartOpen(false)}>
                  Afrekenen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </HardLink>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
